/**
 * Slide data sanitization utilities.
 *
 * This module complements slide-validators.ts by providing sanitization
 * (truncation, clamping) rather than just validation. It ensures slide data
 * stays within safe bounds even when the source data exceeds limits.
 *
 * @see DECK_SYSTEM_AUDIT.md - Section 4.5 "TypeScript Runtime Validation"
 */

import {
  CHART_LIMITS,
  getLimit,
  hasItemLimit,
} from '@digital-twins/deck-ui/foundations/slide-limits'
import {
  getMaxChars,
  hasTextLimit,
} from '@digital-twins/deck-ui/foundations/text-limits'
import { validateSlide, type ValidationResult as ShapeValidationResult } from './slide-validators'
import { sanitizeAgainstSchemaLimits as schemaSanitize, hasSchema, STORED_ALTERNATIVE_PATH } from './schema-loader'
import { normalizeSlideTypeForLimits } from './slide-type-aliases'

export interface SanitizationResult<T> {
  /** The sanitized data */
  data: T
  /** Whether any sanitization was applied */
  modified: boolean
  /** Warnings about what was sanitized */
  warnings: string[]
}

/**
 * Combined validation result that includes both shape validation
 * and sanitization results.
 */
export interface ValidationResult<T = Record<string, unknown>> {
  /** Whether the data is valid (no errors) */
  valid: boolean
  /** Shape validation errors (missing fields, wrong types) */
  errors: string[]
  /** Sanitization warnings (truncated data, clamped values) */
  warnings: string[]
  /** The sanitized data */
  sanitizedData: T
}

/**
 * Sanitize a numeric value.
 * - Returns 0 if NaN, Infinity, or undefined
 * - Optionally clamps to a min/max range
 */
export function sanitizeNumber(
  value: unknown,
  options?: { min?: number; max?: number; fallback?: number | null }
): number | null {
  const { min, max, fallback = null } = options ?? {}

  if (value === undefined || value === null) {
    return fallback
  }

  const num = typeof value === 'number' ? value : Number(value)

  if (!Number.isFinite(num)) {
    return fallback
  }

  let result = num
  if (min !== undefined) result = Math.max(min, result)
  if (max !== undefined) result = Math.min(max, result)

  return result
}

/**
 * Sanitize an array by clamping to max length.
 * Returns a new array (does not mutate).
 */
export function sanitizeArray<T>(
  arr: T[] | undefined | null,
  maxLength: number
): T[] {
  if (!arr) return []
  if (arr.length <= maxLength) return arr
  return arr.slice(0, maxLength)
}

/**
 * Complete text over its LAYOUT budget (slide-limits
 * maxChars) is never cut here. It renders whole, the component's own clamp
 * decides what shows, and the overflow is a warning. Text over its persisted
 * STRUCTURAL ceiling (schema maxLength) fails contract validation instead.
 */
function layoutOverflowWarning(path: string, length: number, maxChars: number): string {
  return `${path}: ${length} chars, over its ${maxChars}-char layout budget; kept whole`
}

/**
 * Sanitize slide data based on slide type.
 * Applies limits from slide-limits.ts and returns warnings for any changes.
 */
export function sanitizeSlideData<T extends { type: string }>(
  data: T
): SanitizationResult<T> {
  const warnings: string[] = []
  const sanitized = { ...data } as Record<string, unknown>
  const truncatedFields: string[] = []
  const slideType = normalizeSlideTypeForLimits(data.type)

  // Sanitize known array fields (template-agnostic)
  const arrayFields = [
    'items',
    'themes',
    'quotes',
    'cards',
    'metrics',
    'segments',
    'rows',
    'columns',
    'approaches',
    'personas',
    'constraints',
    'factors',
  ]

  for (const field of arrayFields) {
    const value = sanitized[field]
    if (Array.isArray(value)) {
      if (!hasItemLimit(field, slideType)) {
        continue
      }
      const limit = getLimit(slideType, field)
      if (value.length > limit) {
        warnings.push(
          `${field}: truncated from ${value.length} to ${limit} items`
        )
        sanitized[field] = value.slice(0, limit)
        truncatedFields.push(field)
      }
    }
  }

  // Sanitize nested arrays (e.g., quotesPerTheme inside themes)
  if (Array.isArray(sanitized.themes)) {
    const themes = sanitized.themes as Array<Record<string, unknown> | null | undefined>
    const hasThemeQuotes = themes.some(
      (theme) =>
        !!theme &&
        typeof theme === 'object' &&
        Array.isArray((theme as Record<string, unknown>).quotes)
    )
    const maxQuotes = hasThemeQuotes
      ? (hasItemLimit('quotesPerTheme', slideType) ? getLimit(slideType, 'quotesPerTheme') : 10)
      : null

    sanitized.themes = themes
      .map((theme, idx) => {
        if (!theme || typeof theme !== 'object') {
          warnings.push(`themes[${idx}]: removed invalid theme entry`)
          return null
        }

        if (
          typeof maxQuotes === 'number' &&
          Array.isArray(theme.quotes) &&
          theme.quotes.length > maxQuotes
        ) {
          warnings.push(
            `theme.quotes: truncated from ${theme.quotes.length} to ${maxQuotes}`
          )
          const result = { ...theme, quotes: theme.quotes.slice(0, maxQuotes) }
          truncatedFields.push('themes.quotes')
          return result
        }
        return theme
      })
      .filter((theme): theme is Record<string, unknown> => theme !== null)
  }

  // Sanitize string fields that are commonly too long
  const stringFieldLimitMap: Record<string, string> = {
    title: 'title',
    subtitle: 'subtitle',
    summary: 'summary',
    description: 'description',
  }
  for (const [field, limitKey] of Object.entries(stringFieldLimitMap)) {
    const value = sanitized[field]
    if (typeof value !== 'string') {
      continue
    }
    const maxChars = getMaxChars(limitKey, slideType)
    if (value.length > maxChars) {
      warnings.push(layoutOverflowWarning(field, value.length, maxChars))
    }
  }

  // Sanitize numeric fields
  const numericFields = ['value', 'score', 'severity', 'frequency', 'delta']
  for (const field of numericFields) {
    const value = sanitized[field]
    if (value !== undefined && typeof value === 'number') {
      if (!Number.isFinite(value)) {
        warnings.push(`${field}: invalid number (${value}), set to null`)
        sanitized[field] = null
      }
    }
  }

  if (truncatedFields.length > 0) {
    sanitized._truncatedFields = truncatedFields
  }

  return {
    data: sanitized as T,
    modified: warnings.length > 0,
    warnings,
  }
}

/** Chart type to limits mapping */
type ChartLimits = {
  maxItems?: number
  maxRows?: number
  maxCols?: number
  maxSegments?: number
}

const CHART_LIMITS_MAP: Record<string, ChartLimits> = {
  horizontalBar: {
    maxItems: CHART_LIMITS.horizontalBarMaxItems,
  },
  matrix: {
    maxRows: CHART_LIMITS.matrixMaxRows,
    maxCols: CHART_LIMITS.matrixMaxCols,
  },
  heatmap: {
    maxRows: CHART_LIMITS.heatmapMaxRows,
    maxCols: CHART_LIMITS.heatmapMaxCols,
  },
  ranking: {
    maxItems: CHART_LIMITS.rankingSegmentedItems,
    maxSegments: CHART_LIMITS.rankingMaxSegments,
  },
  doughnut: {
    maxSegments: CHART_LIMITS.doughnutMaxSegments,
  },
  quadrant: {
    maxItems: CHART_LIMITS.quadrantMaxScatterPoints,
  },
}

/**
 * Sanitize chart-specific data with chart limits.
 */
export function sanitizeChartData<T extends Record<string, unknown>>(
  data: T,
  chartType: string
): SanitizationResult<T> {
  const warnings: string[] = []
  const sanitized = { ...data } as Record<string, unknown>
  const limits = CHART_LIMITS_MAP[chartType]

  if (!limits) {
    return { data, modified: false, warnings: [] }
  }

  // Apply item limit
  if (limits.maxItems && Array.isArray(sanitized.items)) {
    if (sanitized.items.length > limits.maxItems) {
      warnings.push(
        `items: truncated from ${sanitized.items.length} to ${limits.maxItems}`
      )
      sanitized.items = sanitized.items.slice(0, limits.maxItems)
    }
  }

  // Apply row/col limits for matrix-type charts
  if (limits.maxRows && Array.isArray(sanitized.rows)) {
    if (sanitized.rows.length > limits.maxRows) {
      warnings.push(
        `rows: truncated from ${sanitized.rows.length} to ${limits.maxRows}`
      )
      sanitized.rows = sanitized.rows.slice(0, limits.maxRows)
    }
  }

  if (limits.maxCols && Array.isArray(sanitized.columns)) {
    if (sanitized.columns.length > limits.maxCols) {
      warnings.push(
        `columns: truncated from ${sanitized.columns.length} to ${limits.maxCols}`
      )
      sanitized.columns = sanitized.columns.slice(0, limits.maxCols)
    }
  }

  // Apply segment limit
  if (limits.maxSegments && Array.isArray(sanitized.segments)) {
    if (sanitized.segments.length > limits.maxSegments) {
      warnings.push(
        `segments: truncated from ${sanitized.segments.length} to ${limits.maxSegments}`
      )
      sanitized.segments = sanitized.segments.slice(0, limits.maxSegments)
    }
  }

  return {
    data: sanitized as T,
    modified: warnings.length > 0,
    warnings,
  }
}

/**
 * Log sanitization warnings to console in development.
 */
export function logSanitizationWarnings(
  slideType: string,
  warnings: string[]
): void {
  if (warnings.length === 0) return
  if (process.env.NODE_ENV === 'production') return

  console.warn(
    `[Slide Sanitizer] ${slideType}: ${warnings.length} field(s) sanitized`,
    warnings
  )
}

/**
 * Validate and sanitize slide data in one call.
 *
 * This is the primary entry point for slide data validation as specified
 * in the DECK_SYSTEM_AUDIT.md. It:
 * 1. Validates the data shape (required fields, types)
 * 2. Sanitizes the data (truncates arrays, clamps numbers, etc.)
 * 3. Returns combined results with sanitized data
 *
 * @param data - The slide data to validate
 * @returns ValidationResult with errors, warnings, and sanitized data
 *
 * @example
 * const result = validateSlideData(slidePayload);
 * if (!result.valid) {
 *   console.error('Validation errors:', result.errors);
 * }
 * if (result.warnings.length > 0) {
 *   console.warn('Data was sanitized:', result.warnings);
 * }
 * // Use result.sanitizedData for rendering
 */
export function validateSlideData<T extends { type: string }>(
  data: T
): ValidationResult<T> {
  const slideType = normalizeSlideTypeForLimits(data.type)
  const textWarnings: string[] = []

  // Contract validation runs before any display sanitization. Sanitization may
  // make over-budget display payloads easier to render, but it must not turn an
  // invalid canonical payload into a valid one.
  const strictShapeResult = validateSlide(data)
  if (strictShapeResult.errors.length > 0) {
    return {
      valid: false,
      errors: strictShapeResult.errors.map(e => e.message),
      warnings: strictShapeResult.warnings.map(e => e.message),
      sanitizedData: data,
    }
  }

  // Report text over its layout budget after contract validation
  // (never cut, see layoutOverflowWarning).
  // actionTitle is intentionally excluded — its truncation is handled
  // by the headline's own clamp in the renderer so the full text is preserved
  // for accessibility (title attribute) and PDF export.
  const textFieldMap: Record<string, string> = {
    title: 'title',
    subtitle: 'subtitle',
    eyebrow: 'eyebrow',
    actionSubtitle: 'actionSubtitle',
    footerText: 'footerText',
  }

  // Create a mutable copy for sanitization
  const preSanitized = { ...data } as Record<string, unknown>

  // A stored plot that fails its kind's schema is dropped, so the slide
  // renders its legacy content (see validateStoredPlot).
  // A failing alternative is dropped on its own; the plot stays.
  const plotWarnings = strictShapeResult.warnings.filter(w => w.path === 'plot' || w.path.startsWith('plot.'))
  const alternativeWarnings = plotWarnings.filter(w => STORED_ALTERNATIVE_PATH.test(w.path))
  const envelopeWarnings = plotWarnings.filter(w => !STORED_ALTERNATIVE_PATH.test(w.path))
  if (envelopeWarnings.length > 0) {
    delete preSanitized.plot
    textWarnings.push(...envelopeWarnings.map(w => `plot dropped: ${w.message}`))
  } else if (alternativeWarnings.length > 0) {
    const failing = new Set(alternativeWarnings.map(w => Number(STORED_ALTERNATIVE_PATH.exec(w.path)?.[1])))
    const plot = preSanitized.plot as Record<string, unknown>
    const alternatives = (plot.alternatives as Array<unknown>).filter((_, index) => !failing.has(index))
    preSanitized.plot = { ...plot, alternatives }
    textWarnings.push(...alternativeWarnings.map(w => `plot alternative dropped: ${w.message}`))
  }

  for (const [field, limitKey] of Object.entries(textFieldMap)) {
    const value = preSanitized[field]
    if (typeof value === 'string') {
      const maxChars = getMaxChars(limitKey, slideType)
      if (value.length > maxChars) {
        textWarnings.push(layoutOverflowWarning(field, value.length, maxChars))
      }
    }
  }

  const topLevelLimitedFields = new Set(Object.keys(textFieldMap))
  const nestedFieldAliases: Record<string, string> = {
    name: 'itemName',
    label: 'itemName',
    sectionLabel: 'insightLabel',
    text: 'paragraph',
    body: 'paragraph',
    quote: 'qualQuote',
    xAxisLabel: 'xAxisLabel',
    yAxisLabel: 'yAxisLabel',
  }
  const resolveLimitKey = (field: string): string | null => {
    if (hasTextLimit(field, slideType)) return field
    const alias = nestedFieldAliases[field]
    if (alias && hasTextLimit(alias, slideType)) return alias
    return null
  }
  const sanitizeNestedStrings = (node: unknown, path = ''): void => {
    if (Array.isArray(node)) {
      node.forEach((child, index) => {
        sanitizeNestedStrings(child, `${path}[${index}]`)
      })
      return
    }
    if (!node || typeof node !== 'object') return

    const record = node as Record<string, unknown>
    for (const [key, value] of Object.entries(record)) {
      if (key === 'actionTitle') {
        continue
      }
      if (path === '' && topLevelLimitedFields.has(key)) {
        continue
      }
      const currentPath = path ? `${path}.${key}` : key
      if (typeof value === 'string') {
        const limitKey = resolveLimitKey(key)
        if (!limitKey) continue
        const maxChars = getMaxChars(limitKey, slideType)
        if (value.length > maxChars) {
          textWarnings.push(layoutOverflowWarning(currentPath, value.length, maxChars))
        }
        continue
      }
      if (value && typeof value === 'object') {
        sanitizeNestedStrings(value, currentPath)
      }
    }
  }
  sanitizeNestedStrings(preSanitized)

  // Sanitize item names within arrays
  if (Array.isArray(preSanitized.items)) {
    const itemNameLimit = getMaxChars('itemName', slideType)
    preSanitized.items = (preSanitized.items as Array<Record<string, unknown> | null | undefined>)
      .map((item, idx) => {
        if (!item || typeof item !== 'object') {
          textWarnings.push(`items[${idx}]: removed invalid item entry`)
          return null
        }

        if (typeof item.name === 'string' && item.name.length > itemNameLimit) {
          textWarnings.push(layoutOverflowWarning(`items[${idx}].name`, item.name.length, itemNameLimit))
        }
        return item
      })
      .filter((item): item is Record<string, unknown> => item !== null)
  }

  // Pre-sanitize arrays to schema maxItems for display safety after the
  // original payload has already passed contract validation.
  const arrayFieldsToPreSanitize = [
    'items',
    'successMetrics',
    'topConstraints',
    'currentApproach',
    'stayReasons',
    'switchTriggers',
    'themes',
    'quotes',
    'cards',
    'metrics',
    'segments',
    'rows',
    'columns',
  ]

  for (const field of arrayFieldsToPreSanitize) {
    const value = preSanitized[field]
    if (Array.isArray(value)) {
      if (!hasItemLimit(field, slideType)) {
        continue
      }
      const limit = getLimit(slideType, field)
      if (value.length > limit) {
        textWarnings.push(`${field}: truncated from ${value.length} to ${limit} items`)
        preSanitized[field] = value.slice(0, limit)
      }
    }
  }

  // Step 2a: Schema-driven deep sanitization — walks the JSON Schema
  // recursively (including $ref resolution) to truncate strings/arrays
  // that the field-name-based sanitization above doesn't cover (e.g.
  // themes[].keywords[].word).
  if (hasSchema(slideType)) {
    const schemaSanitizeResult = schemaSanitize(slideType, preSanitized as Record<string, unknown>)
    if (schemaSanitizeResult.warnings.length > 0) {
      textWarnings.push(...schemaSanitizeResult.warnings)
    }
  }

  // Step 3: Additional sanitization (arrays, numbers, etc.)
  const sanitizationResult = sanitizeSlideData(preSanitized as T)

  // Combine all warnings
  const allWarnings = [
    ...textWarnings,
    ...sanitizationResult.warnings,
  ]

  // Log in development
  if (allWarnings.length > 0) {
    logSanitizationWarnings(slideType, allWarnings)
  }

  // Preserve slide identity + metric/question trace so chart segment view
  // can target extractors without baking cuts into deck JSON.
  const traceKeys = ['id', 'questionIds', 'metricIds', 'trace'] as const
  for (const key of traceKeys) {
    if (key in data && !(key in (sanitizationResult.data as Record<string, unknown>))) {
      ;(sanitizationResult.data as Record<string, unknown>)[key] = (data as Record<string, unknown>)[key]
    }
  }

  return {
    valid: true,
    errors: [],
    warnings: allWarnings,
    sanitizedData: sanitizationResult.data,
  }
}
